/**
 * Config form transforms
 *
 * Convert between the storage format (nested objects for identifiers,
 * custom_fields, conditions) and the form format (flat arrays) used
 * by the editor UI.
 */

// ---- Factory helpers --------------------------------------------------------

export const EMPTY_CUSTOM_FIELD = () => ({ key: '', selector: '', attribute: 'value' });

export const CONDITION_OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not equals' },
  { value: 'greater_than', label: 'Greater than' },
  { value: 'less_than', label: 'Less than' },
  { value: 'is_empty', label: 'Is empty' },
  { value: 'is_not_empty', label: 'Is not empty' },
];
export const VALUE_OPERATORS = ['equals', 'not_equals', 'greater_than', 'less_than'];

export const CONDITION_MODES = [
  { value: 'allow_when_true', label: 'Only capture when…' },
  { value: 'suppress_when_true', label: "Don't capture when…" },
];

export const EMPTY_CONDITION = () => ({
  enabled: true, selector: '', attribute: 'value', operator: 'equals', value: ''
});

export const EMPTY_IDENTIFIER = () => ({
  label: '', selector: '', attribute: 'value', required: false
});

export const EMPTY_STEP = () => ({
  step_id: '', label: '', application_name: '', url_pattern: '',
  enabled: true,
  trigger_selector: '', trigger_keyboard_selector: '',
  identifiers: [EMPTY_IDENTIFIER()],
  conditions_mode: 'allow_when_true', conditions: [],
  custom_fields: [],
  debug: false
});

export const EMPTY_PROCESS = () => ({
  process_id: '', process_name: '', enabled: true, steps: [EMPTY_STEP()]
});

// ---- Transforms -------------------------------------------------------------

/**
 * Convert raw config JSON → form representation (arrays instead of objects).
 */
export function configToForm(config) {
  if (!config) return [EMPTY_PROCESS()];
  return (config.processes || []).map(p => ({
    process_id: p.process_id || '',
    process_name: p.process_name || '',
    enabled: p.enabled !== false,
    steps: (p.steps || []).map(s => ({
      step_id: s.step_id || '',
      label: s.label || '',
      enabled: s.enabled !== false,
      application_name: s.application_name || '',
      url_pattern: s.url_pattern || '',
      trigger_selector: s.trigger?.selector || '',
      trigger_keyboard_selector: s.trigger?.keyboard_selector || '',
      identifiers: Object.entries(s.identifiers || {}).map(([label, def]) => ({
        label,
        selector: def.selector || '',
        attribute: def.attribute || 'value',
        required: def.required === true
      })),
      conditions_mode: s.conditions_mode || 'allow_when_true',
      conditions: (s.conditions || []).map(c => ({
        enabled: c.enabled !== false,
        selector: c.element?.selector || '',
        attribute: c.element?.attribute || 'value',
        operator: c.operator || 'equals',
        value: c.value || ''
      })),
      custom_fields: Object.entries(s.custom_fields || {}).map(([key, val]) => ({
        key, selector: val.selector || '', attribute: val.attribute || 'value'
      })),
      debug: s.debug === true
    }))
  }));
}

/**
 * Convert form representation → API-ready config JSON.
 */
export function formToConfig(processes) {
  return {
    processes: processes.map(p => ({
      process_id: p.process_id,
      process_name: p.process_name,
      enabled: p.enabled,
      steps: p.steps.map(s => ({
        step_id: s.step_id,
        label: s.label,
        enabled: s.enabled,
        application_name: s.application_name,
        url_pattern: s.url_pattern,
        trigger: {
          type: 'click',
          selector: s.trigger_selector,
          ...(s.trigger_keyboard_selector ? { keyboard_selector: s.trigger_keyboard_selector } : {})
        },
        identifiers: s.identifiers.reduce((acc, id) => {
          if (id.label) {
            acc[id.label] = {
              selector: id.selector,
              attribute: id.attribute,
              ...(id.required ? { required: true } : {})
            };
          }
          return acc;
        }, {}),
        custom_fields: s.custom_fields.reduce((acc, cf) => {
          if (cf.key) acc[cf.key] = { type: 'dom', selector: cf.selector, attribute: cf.attribute };
          return acc;
        }, {}),
        ...(s.conditions.length > 0 ? {
          conditions_mode: s.conditions_mode,
          conditions: s.conditions.map(c => {
            const condObj = {
              enabled: c.enabled,
              element: { type: 'dom', selector: c.selector, attribute: c.attribute },
              operator: c.operator,
            };
            if (VALUE_OPERATORS.includes(c.operator)) {
              condObj.value = c.value;
            }
            return condObj;
          })
        } : {}),
        ...(s.debug ? { debug: true } : {})
      }))
    }))
  };
}
